<script setup lang="ts">
import { Link, usePage } from '@inertiajs/vue3';
import {
    ArrowRight,
    AtSign,
    BadgeCheck,
    Bot,
    CalendarClock,
    ChartSpline,
    Check,
    ChevronRight,
    CircleCheckBig,
    Eye,
    Facebook,
    Globe2,
    Heart,
    Instagram,
    Linkedin,
    LockKeyhole,
    Menu,
    MessageCircle,
    Music2,
    Play,
    Repeat,
    Send,
    ShieldCheck,
    Sparkles,
    Star,
    Wand2,
    X,
    Youtube,
    Zap,
} from 'lucide-vue-next';
import { computed, onUnmounted, ref, watch } from 'vue';
import AppLogoIcon from '@/components/AppLogoIcon.vue';
import MarketingFooter from '@/components/marketing/MarketingFooter.vue';
import SeoHead from '@/components/marketing/SeoHead.vue';
import { dashboard, login, register } from '@/routes';
import type { Team } from '@/types';

defineProps<{ currentTeam?: Team | null; isDev?: boolean }>();

const mobileNavOpen = ref(false);

watch(mobileNavOpen, (open) => {
    if (typeof document === 'undefined') {
        return;
    }
    document.body.style.overflow = open ? 'hidden' : '';
});

onUnmounted(() => {
    if (typeof document !== 'undefined') {
        document.body.style.overflow = '';
    }
});

function closeMobileNav() {
    mobileNavOpen.value = false;
}

const formats = [
    'LinkedIn post',
    'X thread',
    'Instagram caption',
    'TikTok hook',
    'YouTube description',
    'Carousel script',
    'Newsletter intro',
    'Threads post',
    'Facebook post',
    'Short-form script',
];

const channels = ['LinkedIn', 'X', 'Instagram', 'Facebook', 'TikTok', 'YouTube', 'Threads'];

const loop = [
    {
        icon: Wand2,
        title: 'Create',
        desc: 'One prompt or source becomes channel-ready posts in your brand voice.',
        color: 'from-violet-500 to-fuchsia-500',
    },
    {
        icon: CalendarClock,
        title: 'Schedule',
        desc: 'Drop each piece on the calendar. Edit freely — the queue keeps up.',
        color: 'from-sky-500 to-cyan-500',
    },
    {
        icon: Send,
        title: 'Publish',
        desc: 'Native publishing to seven platforms, formatted per channel.',
        color: 'from-teal-500 to-emerald-500',
    },
    {
        icon: ChartSpline,
        title: 'Measure',
        desc: 'Likes, comments, shares, and views sync back automatically.',
        color: 'from-amber-500 to-orange-500',
    },
    {
        icon: Bot,
        title: 'Learn',
        desc: 'Breakout posts are detected — and become the brief for the next one.',
        color: 'from-indigo-500 to-violet-500',
    },
];

const modules = [
    {
        icon: Wand2,
        title: 'Content Studio',
        tag: 'Create',
        desc: 'Write a prompt — or drop a podcast, video, or article — and get a full set of platform-native posts written in your voice.',
        points: ['15+ formats from one source', 'Brand voice baked into every draft', 'Regenerate with context from prior pieces'],
        iconBg: 'from-violet-500 to-fuchsia-500',
        glow: 'group-hover:shadow-violet-500/20',
    },
    {
        icon: CalendarClock,
        title: 'Calendar',
        tag: 'Publish',
        desc: 'A calendar that publishes for you. Connect every channel once, then queue weeks of content in an afternoon.',
        points: ['Seven platforms, one calendar', 'Edit any post before it ships — with a diff', 'Automatic retries and status tracking'],
        iconBg: 'from-teal-500 to-emerald-500',
        glow: 'group-hover:shadow-teal-500/20',
    },
    {
        icon: ChartSpline,
        title: 'Analytics',
        tag: 'Measure',
        desc: "Engagement flows back from every platform — so you see which post won, on which channel, and why.",
        points: ['Reactions, comments & views per post', 'Top-post leaderboards by channel', 'Real comments, pulled into one feed'],
        iconBg: 'from-amber-500 to-orange-500',
        glow: 'group-hover:shadow-amber-500/20',
    },
    {
        icon: Bot,
        title: 'Autopilot',
        tag: 'Repeat',
        desc: 'When a post breaks out, CreativeTint notices — and drafts follow-ups in the same vein before you even open the app.',
        points: ['Breakout detection on live engagement', 'Follow-ups drafted in your brand voice', 'You approve; nothing ships without you'],
        iconBg: 'from-indigo-500 to-violet-500',
        glow: 'group-hover:shadow-indigo-500/20',
    },
];

const activity = [
    { time: '02:00', text: 'Synced engagement for 34 published posts' },
    { time: '02:05', text: 'Breakout detected — "Onboarding launch" at 3.4× your average', hot: true },
    { time: '02:06', text: 'Drafted 3 follow-up posts in your brand voice' },
    { time: '08:00', text: 'Follow-ups waiting for your approval', cta: true },
];

const steps = [
    {
        n: '01',
        t: 'Teach it your brand',
        d: 'A two-minute brand profile — your niche, audience, and tone. Every draft CreativeTint writes speaks like you.',
    },
    {
        n: '02',
        t: 'Create and schedule',
        d: 'One prompt becomes a week of channel-ready posts. Drop them on the calendar and the queue publishes for you.',
    },
    {
        n: '03',
        t: 'Let the loop learn',
        d: 'Engagement syncs back around the clock. Winners are detected, and the next posts draft themselves from what worked.',
    },
];

const pricing = {
    name: 'Pro',
    price: 19,
    description: '7-day free trial, then $19/mo. Everything included. Cancel anytime.',
    features: [
        '7-day free trial — full Pro access',
        '1,000 AI credits / month',
        'Unlimited manual create, edit & schedule',
        'Unlimited publishing to all 7 platforms',
        'Analytics, comments & reactions sync',
        'Autopilot follow-ups (fair-use limit)',
        'Brand voice & team workspace',
    ],
    cta: 'Start 7-day free trial',
};

const faqs = [
    {
        q: 'Is there really only one plan?',
        a: "Yes — a 7-day free trial, then $19 a month with 1,000 AI credits. Manual create, edit, schedule, and publish are unlimited. AI generation and Autopilot follow-ups use credits (and a fair-use limit). Cancel anytime.",
    },
    {
        q: 'Which platforms can I publish to?',
        a: 'LinkedIn, X, Instagram, Facebook, TikTok, YouTube, and Threads. Connect each channel once and CreativeTint publishes natively — formatted the way each platform expects.',
    },
    {
        q: 'What does Autopilot actually do?',
        a: 'It syncs engagement around the clock, spots posts that outperform your average, and drafts follow-ups in your brand voice. Everything lands in your queue for approval — nothing ships without you.',
    },
    {
        q: 'Will it write in our brand voice?',
        a: 'Yes. Your brand profile — niche, audience, tone, and do-nots — is baked into every draft. The more you publish, the more the loop learns which of your angles actually land.',
    },
    {
        q: 'Can I turn a podcast, webinar, or blog into posts?',
        a: 'Yes. Drop a source into Content Studio and CreativeTint turns it into a set of channel-ready posts — LinkedIn, X threads, captions, hooks, and more — instead of starting from a blank page.',
    },
    {
        q: 'How is this different from ChatGPT + a scheduler?',
        a: 'ChatGPT drafts once and forgets. Schedulers push and go quiet. CreativeTint closes the loop: create → publish → measure → learn, so the next brief comes from what actually performed.',
    },
    {
        q: 'Can my team collaborate in one workspace?',
        a: 'Yes. Invite teammates to your workspace, share brand voice, review drafts, and manage the calendar together — without bouncing files between tools.',
    },
    {
        q: 'Do I have to use Autopilot?',
        a: 'No. Autopilot is optional. You can create, edit, schedule, and publish manually every day. Turn Autopilot on when you want breakout posts to seed the next drafts.',
    },
    {
        q: 'Can I edit posts after they are scheduled?',
        a: 'Yes. Open any scheduled post, edit copy or timing, and see a clear diff of what changed before it goes live.',
    },
    {
        q: 'What analytics and comments do you pull back?',
        a: 'Reactions, comments, shares, and views sync from connected platforms into one place — so you can see winners by channel and read the conversation without hopping apps.',
    },
    {
        q: 'Who owns the content we create?',
        a: 'You do. Posts, drafts, and brand settings stay yours. Cancel anytime — what you created remains available to export or keep using while your account is active.',
    },
    {
        q: 'Is there a free way to try it?',
        a: 'Yes — every new workspace gets a 7-day explore trial so you can connect channels and use the scheduler. AI generation unlocks when you subscribe to Pro ($19/mo).',
    },
    {
        q: 'Will CreativeTint post without my approval?',
        a: 'Never for Autopilot follow-ups. Drafts wait in your queue until you approve. Scheduled posts you set yourself publish at the times you chose — you stay in control.',
    },
    {
        q: 'What happens if a publish fails?',
        a: 'We track status per channel, retry when it makes sense, and surface failures clearly so you can fix a connection or reschedule without guessing what went wrong.',
    },
    {
        q: 'Can agencies or freelancers use this for clients?',
        a: 'Yes — set brand voice per workspace, schedule across client channels, and use performance to brief the next wave. Ideal when you manage multiple content calendars.',
    },
    {
        q: 'Do I need design or video editing skills?',
        a: "No for the writing and publishing loop. If you can describe the idea — or drop a source — CreativeTint drafts channel-ready copy you can tweak before it ships.",
    },
];

const testimonials = [
    {
        quote: 'We stopped arguing about what to post. CreativeTint shows us the winner and has the follow-up drafted before Monday standup.',
        name: 'Lena K.',
        role: 'Head of Marketing · SaaS',
        avatar: 'from-violet-400 to-fuchsia-400',
    },
    {
        quote: 'One podcast episode became eleven posts across five channels. The LinkedIn one blew up — and the autopilot had a sequel waiting by morning.',
        name: 'Daniel R.',
        role: 'Podcast host',
        avatar: 'from-cyan-400 to-teal-400',
    },
    {
        quote: "It's the first tool where the analytics actually do something. The create-similar loop doubled our engagement in six weeks.",
        name: 'Priya S.',
        role: 'DTC marketer',
        avatar: 'from-amber-400 to-rose-400',
    },
];

const topPostBars = [34, 52, 41, 68, 55, 88, 100];

const page = usePage<{
    seo?: { appUrl: string; name: string; description: string; twitter: string };
}>();

const seoTitle = 'CreativeTint — AI Social Content Loop | Create, Publish, Learn & Automate';
const seoDescription =
    'Create channel-ready posts, publish natively to LinkedIn, X, Instagram, Facebook, TikTok, YouTube & Threads, sync engagement, and let Autopilot draft follow-ups from what actually worked. One loop. $19/mo.';

const jsonLd = computed(() => {
    const base = (page.props.seo?.appUrl ?? '').replace(/\/$/, '');
    const orgId = `${base}/#organization`;
    const appId = `${base}/#software`;
    const websiteId = `${base}/#website`;

    return [
        {
            '@context': 'https://schema.org',
            '@type': 'Organization',
            '@id': orgId,
            name: 'CreativeTint',
            url: `${base}/`,
            logo: `${base}/icons/android-chrome-512x512.png`,
            email: 'support@creativetint.com',
            description: seoDescription,
            foundingDate: '2026',
            sameAs: [],
            contactPoint: [
                {
                    '@type': 'ContactPoint',
                    contactType: 'customer support',
                    email: 'support@creativetint.com',
                    url: `${base}/contact`,
                    availableLanguage: ['English'],
                },
            ],
        },
        {
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            '@id': websiteId,
            url: `${base}/`,
            name: 'CreativeTint',
            description: seoDescription,
            publisher: { '@id': orgId },
            inLanguage: 'en-US',
        },
        {
            '@context': 'https://schema.org',
            '@type': 'SoftwareApplication',
            '@id': appId,
            name: 'CreativeTint',
            applicationCategory: 'BusinessApplication',
            applicationSubCategory: 'Social Media Management',
            operatingSystem: 'Web',
            url: `${base}/`,
            image: `${base}/images/og-default.png`,
            description: seoDescription,
            featureList: [
                'AI content generation in your brand voice',
                'Native publishing to LinkedIn, X, Instagram, Facebook, TikTok, YouTube, Threads',
                'Content calendar and scheduling',
                'Engagement, comments, and metrics sync',
                'Autopilot follow-up drafts from breakout posts',
            ],
            offers: {
                '@type': 'Offer',
                price: '19.00',
                priceCurrency: 'USD',
                availability: 'https://schema.org/InStock',
                url: `${base}/#pricing`,
                description: 'Pro plan — everything included. Cancel anytime.',
            },
            publisher: { '@id': orgId },
            brand: { '@id': orgId },
        },
        {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            '@id': `${base}/#faq`,
            mainEntity: faqs.map((f) => ({
                '@type': 'Question',
                name: f.q,
                acceptedAnswer: {
                    '@type': 'Answer',
                    text: f.a,
                },
            })),
        },
        {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            '@id': `${base}/#webpage`,
            url: `${base}/`,
            name: seoTitle,
            description: seoDescription,
            isPartOf: { '@id': websiteId },
            about: { '@id': appId },
            primaryImageOfPage: `${base}/images/og-default.png`,
            inLanguage: 'en-US',
        },
    ];
});
</script>

<template>
    <SeoHead
        :title="seoTitle"
        :description="seoDescription"
        path="/"
        :json-ld="jsonLd"
    />

    <div class="relative min-h-screen overflow-x-clip bg-white text-foreground dark:bg-zinc-950">
        <!-- Quiet opening lift — brand energy without a gray slab -->
        <div class="pointer-events-none absolute inset-x-0 top-0 h-[520px] bg-[radial-gradient(ellipse_70%_60%_at_50%_-10%,hsl(262_70%_55%/0.14),transparent_70%)]" />

        <!-- Nav -->
        <header class="sticky top-0 z-40 border-b border-zinc-200 bg-white/95 shadow-sm backdrop-blur-xl dark:border-zinc-800 dark:bg-zinc-950/95">
            <div class="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 sm:px-6 sm:py-3.5">
                <Link href="/" class="flex min-w-0 items-center gap-2 sm:gap-2.5">
                    <AppLogoIcon uid="welcome-nav" class="size-7 flex-none sm:size-8" />
                    <span class="font-display truncate text-base font-bold tracking-tight sm:text-lg">CreativeTint</span>
                </Link>

                <nav class="hidden items-center gap-8 text-sm font-semibold text-zinc-800 md:flex dark:text-zinc-100">
                    <a href="#modules" class="transition hover:text-violet-600 dark:hover:text-violet-300">Features</a>
                    <a href="#pricing" class="transition hover:text-violet-600 dark:hover:text-violet-300">Pricing</a>
                    <a href="#faq" class="transition hover:text-violet-600 dark:hover:text-violet-300">FAQ</a>
                </nav>

                <div class="flex items-center gap-1.5 sm:gap-2">
                    <template v-if="currentTeam">
                        <Link
                            :href="dashboard(currentTeam.slug)"
                            class="inline-flex items-center gap-1.5 rounded-lg bg-foreground px-3 py-2 text-sm font-semibold text-background transition hover:opacity-90 sm:px-4"
                        >
                            <span class="hidden sm:inline">Open dashboard</span>
                            <span class="sm:hidden">Dashboard</span>
                            <ArrowRight class="size-4" />
                        </Link>
                    </template>
                    <template v-else>
                        <Link
                            v-if="isDev"
                            href="/dev/login"
                            class="hidden items-center gap-1.5 rounded-lg border border-zinc-300 px-3 py-1.5 text-xs font-semibold text-zinc-800 hover:border-zinc-500 dark:border-zinc-700 dark:text-zinc-100 sm:inline-flex"
                        >
                            <Sparkles class="size-3" /> Dev login
                        </Link>
                        <Link
                            :href="login()"
                            class="hidden rounded-lg px-3 py-2 text-sm font-semibold text-zinc-800 transition hover:text-violet-600 dark:text-zinc-100 dark:hover:text-violet-300 sm:inline"
                        >
                            Log in
                        </Link>
                        <Link
                            :href="register()"
                            class="inline-flex items-center gap-1.5 rounded-lg bg-foreground px-3 py-2 text-sm font-semibold text-background transition hover:opacity-90 sm:px-4"
                        >
                            Get started
                            <ArrowRight class="size-4" />
                        </Link>
                    </template>
                    <button
                        type="button"
                        class="inline-flex size-10 items-center justify-center rounded-lg border border-zinc-200 text-zinc-800 md:hidden dark:border-zinc-700 dark:text-zinc-100"
                        :aria-expanded="mobileNavOpen"
                        aria-controls="mobile-nav"
                        :aria-label="mobileNavOpen ? 'Close menu' : 'Open menu'"
                        @click="mobileNavOpen = !mobileNavOpen"
                    >
                        <X v-if="mobileNavOpen" class="size-5" />
                        <Menu v-else class="size-5" />
                    </button>
                </div>
            </div>

            <div
                v-if="mobileNavOpen"
                id="mobile-nav"
                class="border-t border-zinc-200 bg-white md:hidden dark:border-zinc-800 dark:bg-zinc-950"
            >
                <nav class="mx-auto flex max-w-7xl flex-col gap-1 px-4 py-3 sm:px-6">
                    <a href="#modules" class="rounded-lg px-3 py-3 text-base font-semibold text-zinc-800 hover:bg-violet-50 dark:text-zinc-100 dark:hover:bg-violet-950/40" @click="closeMobileNav">Features</a>
                    <a href="#pricing" class="rounded-lg px-3 py-3 text-base font-semibold text-zinc-800 hover:bg-violet-50 dark:text-zinc-100 dark:hover:bg-violet-950/40" @click="closeMobileNav">Pricing</a>
                    <a href="#faq" class="rounded-lg px-3 py-3 text-base font-semibold text-zinc-800 hover:bg-violet-50 dark:text-zinc-100 dark:hover:bg-violet-950/40" @click="closeMobileNav">FAQ</a>
                    <Link
                        v-if="!currentTeam"
                        :href="login()"
                        class="mt-1 rounded-lg px-3 py-3 text-base font-semibold text-zinc-800 hover:bg-violet-50 dark:text-zinc-100 dark:hover:bg-violet-950/40"
                        @click="closeMobileNav"
                    >
                        Log in
                    </Link>
                </nav>
            </div>
        </header>

        <main id="main-content">
        <!-- Hero — full-bleed canvas for décor; content stays max-w-7xl -->
        <section class="relative w-full overflow-hidden bg-white dark:bg-zinc-950">
            <!-- Full-viewport decorative constellation (lg+) -->
            <div class="pointer-events-none absolute inset-0 z-0 hidden lg:block" aria-hidden="true">
                <!-- Edge curves -->
                <svg class="absolute top-0 left-0 h-[70%] w-72 text-violet-400/15" viewBox="0 0 190 280" fill="none" preserveAspectRatio="xMinYMin meet">
                    <path d="M172 14C78 44 38 108 24 252" stroke="currentColor" stroke-width="1.5" stroke-dasharray="5 8" />
                    <circle cx="135" cy="32" r="3.5" fill="currentColor" />
                    <circle cx="49" cy="160" r="2.5" fill="currentColor" />
                </svg>
                <svg class="absolute top-4 right-0 h-[70%] w-72 -scale-x-100 text-cyan-400/15" viewBox="0 0 190 280" fill="none" preserveAspectRatio="xMinYMin meet">
                    <path d="M172 14C78 44 38 108 24 252" stroke="currentColor" stroke-width="1.5" stroke-dasharray="5 8" />
                    <circle cx="135" cy="32" r="3.5" fill="currentColor" />
                    <circle cx="49" cy="160" r="2.5" fill="currentColor" />
                </svg>
                <svg class="absolute bottom-6 left-1/2 h-28 w-[min(90vw,56rem)] -translate-x-1/2 text-violet-400/12" viewBox="0 0 680 100" fill="none">
                    <path d="M8 72C120 18 230 14 340 48C450 82 560 84 672 28" stroke="currentColor" stroke-width="1.5" stroke-dasharray="4 8" />
                </svg>

                <!-- Far left column -->
                <div class="absolute top-[6%] left-[2%] rotate-[-12deg] xl:left-[4%]">
                    <div class="animate-float-soft flex size-16 items-center justify-center rounded-3xl bg-emerald-500/8 text-emerald-500/25">
                        <ShieldCheck class="size-9" stroke-width="1.35" />
                    </div>
                </div>
                <div class="absolute top-[18%] left-[1%] rotate-[-6deg] xl:left-[3%]">
                    <div class="animate-float-soft flex size-11 items-center justify-center rounded-xl border border-pink-400/15 bg-pink-500/5 text-pink-500/28 [animation-delay:-2.4s]">
                        <Instagram class="size-5" />
                    </div>
                </div>
                <div class="absolute top-[38%] left-[2%] rotate-6 xl:left-[5%]">
                    <div class="animate-float-soft flex size-12 items-center justify-center rounded-lg border border-sky-400/15 bg-sky-500/5 text-sky-600/28 [animation-delay:-4s]">
                        <Linkedin class="size-5" />
                    </div>
                </div>
                <div class="absolute top-[52%] left-[1%] rotate-[-8deg] xl:left-[3.5%]">
                    <div class="animate-float-soft-slow flex size-14 items-center justify-center rounded-full bg-violet-500/8 text-violet-500/25 [animation-delay:-2s]">
                        <CircleCheckBig class="size-8" stroke-width="1.35" />
                    </div>
                </div>
                <div class="absolute bottom-[28%] left-[2%] rotate-[-5deg] xl:left-[4%]">
                    <div class="animate-float-soft flex size-11 items-center justify-center rounded-full border border-violet-400/15 bg-violet-500/5 text-violet-600/28 [animation-delay:-5s]">
                        <AtSign class="size-5" />
                    </div>
                </div>
                <div class="absolute bottom-[12%] left-[3%] rotate-[-8deg] xl:left-[6%]">
                    <div class="animate-float-soft flex size-12 items-center justify-center rounded-2xl bg-teal-500/8 text-teal-500/22 [animation-delay:-3.2s]">
                        <CalendarClock class="size-6" stroke-width="1.35" />
                    </div>
                </div>

                <!-- Inner-left gutter (between edge and content) -->
                <div class="absolute top-[10%] left-[12%] rotate-8 xl:left-[14%]">
                    <div class="animate-float-soft-slow flex size-9 items-center justify-center text-emerald-500/25 [animation-delay:-1.5s]">
                        <BadgeCheck class="size-7" />
                    </div>
                </div>
                <div class="absolute top-[44%] left-[11%] rotate-12 xl:left-[13%]">
                    <div class="animate-float-soft-slow flex size-10 items-center justify-center rounded-xl border border-cyan-400/15 bg-cyan-500/5 text-cyan-600/28 [animation-delay:-2.8s]">
                        <Music2 class="size-5" />
                    </div>
                </div>

                <!-- Far right column -->
                <div class="absolute top-[6%] right-[2%] rotate-12 xl:right-[4%]">
                    <div class="animate-float-soft-slow flex size-16 items-center justify-center rounded-3xl bg-cyan-500/8 text-cyan-500/25">
                        <Globe2 class="size-9" stroke-width="1.35" />
                    </div>
                </div>
                <div class="absolute top-[18%] right-[1%] rotate-8 xl:right-[3%]">
                    <div class="animate-float-soft-slow flex size-11 items-center justify-center rounded-xl border border-red-400/15 bg-red-500/5 text-red-500/28 [animation-delay:-1.2s]">
                        <Youtube class="size-5" />
                    </div>
                </div>
                <div class="absolute top-[38%] right-[2%] rotate-[-6deg] xl:right-[5%]">
                    <div class="animate-float-soft flex size-12 items-center justify-center rounded-lg border border-blue-400/15 bg-blue-500/5 text-blue-600/28 [animation-delay:-3.5s]">
                        <Facebook class="size-5" />
                    </div>
                </div>
                <div class="absolute top-[52%] right-[1%] rotate-10 xl:right-[3.5%]">
                    <div class="animate-float-soft-slow flex size-14 items-center justify-center rounded-full bg-amber-500/8 text-amber-500/25 [animation-delay:-0.8s]">
                        <Zap class="size-8" stroke-width="1.35" />
                    </div>
                </div>
                <div class="absolute right-[2%] bottom-[28%] rotate-5 xl:right-[4%]">
                    <div class="animate-float-soft flex size-11 items-center justify-center rounded-xl border border-zinc-400/15 bg-zinc-500/5 text-zinc-500/28 [animation-delay:-1.8s]">
                        <Send class="size-5" />
                    </div>
                </div>
                <div class="absolute right-[3%] bottom-[12%] rotate-8 xl:right-[6%]">
                    <div class="animate-float-soft-slow flex size-12 items-center justify-center rounded-2xl bg-orange-500/8 text-orange-500/22 [animation-delay:-0.5s]">
                        <ChartSpline class="size-6" stroke-width="1.35" />
                    </div>
                </div>

                <!-- Inner-right gutter -->
                <div class="absolute top-[10%] right-[12%] rotate-[-10deg] xl:right-[14%]">
                    <div class="animate-float-soft flex size-9 items-center justify-center text-violet-500/25 [animation-delay:-3s]">
                        <LockKeyhole class="size-7" />
                    </div>
                </div>
                <div class="absolute top-[44%] right-[11%] rotate-[-12deg] xl:right-[13%]">
                    <div class="animate-float-soft-slow flex size-10 items-center justify-center rounded-full border border-fuchsia-400/15 bg-fuchsia-500/5 text-fuchsia-500/28 [animation-delay:-4.4s]">
                        <Sparkles class="size-5" />
                    </div>
                </div>

                <!-- Soft mid / background accents (behind content, very faint) -->
                <div class="absolute top-[22%] left-[22%] rotate-[-4deg] opacity-60">
                    <div class="animate-float-soft-slow flex size-8 items-center justify-center text-violet-400/20 [animation-delay:-6s]">
                        <Bot class="size-6" stroke-width="1.35" />
                    </div>
                </div>
                <div class="absolute top-[22%] right-[22%] rotate-6 opacity-60">
                    <div class="animate-float-soft flex size-8 items-center justify-center text-cyan-400/20 [animation-delay:-5.2s]">
                        <Wand2 class="size-6" stroke-width="1.35" />
                    </div>
                </div>
                <div class="absolute bottom-[8%] left-1/2 -translate-x-1/2 rotate-[-3deg] opacity-50">
                    <div class="animate-float-soft-slow flex size-9 items-center justify-center text-fuchsia-400/18 [animation-delay:-1s]">
                        <Repeat class="size-6" stroke-width="1.35" />
                    </div>
                </div>
            </div>

            <!-- Constrained content -->
            <div class="relative z-10 mx-auto max-w-7xl px-4 pt-6 pb-8 text-center sm:px-6 sm:pt-8 md:pt-10">
                <div class="mx-auto inline-flex max-w-full items-center gap-2 rounded-full border border-border/60 bg-card/70 px-3 py-1.5 text-left text-[11px] font-medium text-muted-foreground sm:px-4 sm:text-xs">
                    <span class="flex size-5 flex-none items-center justify-center rounded-full bg-gradient-to-br from-violet-500/40 to-cyan-500/40 text-violet-500 dark:text-violet-200">
                        <Bot class="size-3" />
                    </span>
                    <span class="min-w-0 leading-snug">New — Autopilot drafts follow-ups to your breakout posts</span>
                </div>

                <h1 class="font-display mx-auto mt-5 max-w-4xl text-[2.15rem] leading-[1.08] font-extrabold tracking-tight text-balance sm:mt-6 sm:text-5xl md:text-[5rem] md:leading-[1.04]">
                    Create. Publish. Repeat.
                    <span class="ct-gradient-text block">Learn & Automate.</span>
                </h1>

                <p class="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">
                    CreativeTint turns one idea into channel-ready posts, publishes
                    them across seven platforms, reads the reactions — and drafts
                    your next winner from what actually worked.
                </p>

                <div class="mx-auto mt-6 flex w-full max-w-md flex-col items-stretch gap-3 sm:max-w-none sm:flex-row sm:items-center sm:justify-center">
                    <Link
                        :href="register()"
                        class="group inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-br from-violet-500 via-fuchsia-500 to-cyan-500 px-6 py-3.5 text-base font-semibold text-white transition hover:scale-[1.02] hover:opacity-95 sm:px-8"
                    >
                        Start your 7-day free trial
                        <ArrowRight class="size-4 transition group-hover:translate-x-0.5" />
                    </Link>
                    <a
                        href="#loop"
                        class="inline-flex items-center justify-center gap-2 rounded-full border border-border bg-card/70 px-6 py-3.5 text-base font-medium transition hover:bg-card sm:px-8"
                    >
                        <Play class="size-4" /> See it in action
                    </a>
                </div>

                <div class="mt-4 flex flex-col items-center justify-center gap-2 text-sm text-muted-foreground sm:flex-row sm:gap-3">
                    <div class="flex -space-x-1.5">
                        <div class="size-7 rounded-full bg-gradient-to-br from-violet-400 to-fuchsia-400 ring-2 ring-background" />
                        <div class="size-7 rounded-full bg-gradient-to-br from-cyan-400 to-teal-400 ring-2 ring-background" />
                        <div class="size-7 rounded-full bg-gradient-to-br from-amber-400 to-orange-400 ring-2 ring-background" />
                        <div class="size-7 rounded-full bg-gradient-to-br from-pink-400 to-rose-400 ring-2 ring-background" />
                    </div>
                    <span class="max-w-xs text-center leading-snug sm:max-w-none">Built for marketing teams that are <strong class="font-semibold text-foreground">done guessing</strong></span>
                </div>

                <!-- Loop ribbon -->
                <div class="relative mx-auto mt-5 max-w-6xl sm:mt-8">
                    <!-- Mobile: connected step chain (no orbit animation) -->
                    <div class="sm:hidden">
                        <div class="relative left-1/2 w-screen max-w-[100vw] -translate-x-1/2 px-2">
                            <div class="flex w-full items-start">
                                <template v-for="(s, i) in loop" :key="s.title">
                                    <div class="flex min-w-0 flex-1 flex-col items-center text-center">
                                        <div
                                            class="flex size-12 items-center justify-center rounded-full bg-gradient-to-br text-white"
                                            :class="s.color"
                                        >
                                            <component :is="s.icon" class="size-5" />
                                        </div>
                                        <span class="font-display mt-1.5 text-[10px] leading-tight font-bold tracking-tight">{{ s.title }}</span>
                                    </div>
                                    <div v-if="i < loop.length - 1" class="mt-4 flex flex-none items-center self-start">
                                        <ChevronRight class="size-3.5 text-violet-400/80" />
                                    </div>
                                </template>
                            </div>
                        </div>
                        <div class="mt-4 flex justify-center">
                            <span class="inline-flex max-w-full items-center gap-2 rounded-full border border-violet-400/40 bg-violet-500/10 px-3 py-2 text-center text-xs font-semibold text-violet-600 dark:text-violet-300">
                                <Repeat class="size-3.5 flex-none" />
                                <span class="min-w-0 leading-snug">Step 5 writes the brief for step 1</span>
                            </span>
                        </div>
                    </div>

                    <!-- Tablet / desktop cards -->
                    <div class="hidden gap-3 text-left sm:grid sm:grid-cols-2 lg:grid-cols-[1fr_auto_1fr_auto_1fr_auto_1fr_auto_1fr] lg:items-stretch">
                        <template v-for="(s, i) in loop" :key="s.title">
                            <div class="relative rounded-2xl border border-border/60 bg-card/80 p-5 shadow-sm backdrop-blur transition duration-300 hover:-translate-y-1 hover:shadow-lg">
                                <div class="flex items-start justify-between gap-3">
                                    <div
                                        class="flex size-11 items-center justify-center rounded-xl bg-gradient-to-br text-white shadow-md"
                                        :class="s.color"
                                    >
                                        <component :is="s.icon" class="size-5" />
                                    </div>
                                    <span class="font-display text-3xl font-extrabold tracking-tight text-muted-foreground/20">0{{ i + 1 }}</span>
                                </div>
                                <div class="font-display mt-4 text-base font-bold tracking-tight">{{ s.title }}</div>
                                <p class="mt-1.5 text-[13px] leading-relaxed text-muted-foreground">{{ s.desc }}</p>
                            </div>
                            <div v-if="i < 4" class="hidden items-center lg:flex">
                                <div class="flex size-7 flex-none items-center justify-center rounded-full border border-violet-400/40 bg-violet-500/10 text-violet-500 dark:text-violet-300">
                                    <ChevronRight class="size-3.5" />
                                </div>
                            </div>
                        </template>
                    </div>

                    <div class="relative mt-1 hidden lg:block">
                        <svg class="h-20 w-full" viewBox="0 0 1000 80" fill="none" preserveAspectRatio="none">
                            <path d="M 908 6 C 908 68, 92 68, 92 14" stroke="url(#ct-loop-grad)" stroke-width="2" class="ct-dash-flow" />
                            <path d="M 85 22 L 92 10 L 99 22" stroke="#8b5cf6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                            <defs>
                                <linearGradient id="ct-loop-grad" x1="0" y1="0" x2="1000" y2="0" gradientUnits="userSpaceOnUse">
                                    <stop stop-color="#8b5cf6" />
                                    <stop offset="0.5" stop-color="#06b6d4" />
                                    <stop offset="1" stop-color="#8b5cf6" />
                                </linearGradient>
                            </defs>
                        </svg>
                        <span class="absolute top-[55%] left-1/2 inline-flex -translate-x-1/2 -translate-y-1/2 items-center gap-2 rounded-full border border-violet-400/40 bg-background px-4 py-1.5 text-sm font-semibold text-violet-600 shadow-sm dark:text-violet-300">
                            <Repeat class="size-3.5" />
                            Step 5 writes the brief for step 1 — that's the loop
                        </span>
                    </div>
                </div>
            </div>
        </section>

        <!-- Product proof — violet→cyan: the loop itself -->
        <section id="loop" class="ct-band-loop relative scroll-mt-20 overflow-hidden">
            <div class="relative mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-16 md:py-20">
                <div class="mx-auto max-w-2xl text-center">
                    <p class="text-xs font-bold tracking-[0.2em] text-violet-500 uppercase dark:text-violet-300">
                        The CreativeTint loop
                    </p>
                    <h2 class="font-display mt-3 text-3xl font-extrabold tracking-tight text-balance sm:text-4xl md:text-5xl">
                        Most tools stop at publishing.
                        <span class="ct-gradient-text">Ours starts learning.</span>
                    </h2>
                    <p class="mt-4 text-base text-muted-foreground sm:text-lg">
                        Schedulers push posts out and go quiet. CreativeTint closes the loop — every reaction feeds the next piece of content.
                    </p>
                </div>

                <div class="relative mx-auto mt-8 max-w-6xl sm:mt-10">
                    <div class="relative rounded-2xl border border-violet-200/50 bg-white/80 p-3 shadow-xl shadow-violet-500/10 backdrop-blur-md sm:rounded-3xl sm:p-4 md:p-5 dark:border-violet-500/20 dark:bg-zinc-900/70">
                        <div class="grid gap-3 lg:grid-cols-[1fr_auto_1fr_auto_1fr] lg:items-stretch">
                            <div class="flex flex-col rounded-2xl border border-border/60 bg-background/80 p-4 text-left shadow-sm sm:p-5">
                                <div class="flex flex-wrap items-center justify-between gap-2">
                                    <div class="flex min-w-0 items-center gap-2.5">
                                        <div class="flex size-8 flex-none items-center justify-center rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white">
                                            <Wand2 class="size-4" />
                                        </div>
                                        <span class="font-display text-sm font-bold">Content Studio</span>
                                    </div>
                                    <span class="rounded-full border border-border/60 px-2.5 py-0.5 text-[10px] font-semibold tracking-wider text-muted-foreground uppercase">01 · Create</span>
                                </div>
                                <div class="mt-4 rounded-xl border border-border/60 bg-card/70 p-3.5 text-left text-[13px] leading-relaxed text-muted-foreground">
                                    Turn our onboarding revamp into launch posts — confident, a little playful…
                                </div>
                                <div class="mt-3 flex flex-wrap gap-1.5">
                                    <span class="inline-flex items-center gap-1 rounded-full border border-violet-400/40 bg-violet-500/10 px-2.5 py-1 text-[11px] font-medium text-violet-600 dark:text-violet-300">
                                        <Check class="size-3" /> LinkedIn post
                                    </span>
                                    <span class="inline-flex items-center gap-1 rounded-full border border-violet-400/40 bg-violet-500/10 px-2.5 py-1 text-[11px] font-medium text-violet-600 dark:text-violet-300">
                                        <Check class="size-3" /> X thread
                                    </span>
                                    <span class="inline-flex items-center rounded-full border border-border/60 px-2.5 py-1 text-[11px] font-medium text-muted-foreground">
                                        IG caption
                                    </span>
                                </div>
                                <div class="mt-auto pt-4">
                                    <div class="inline-flex w-full items-center justify-center gap-1.5 rounded-xl bg-gradient-to-br from-violet-500 to-cyan-500 px-4 py-2.5 text-sm font-semibold text-white shadow-md shadow-violet-500/25">
                                        <Sparkles class="size-4" /> Generate
                                    </div>
                                </div>
                            </div>

                            <div class="hidden items-center lg:flex">
                                <div class="flex size-8 items-center justify-center rounded-full border border-border/60 bg-card text-muted-foreground">
                                    <ChevronRight class="size-4" />
                                </div>
                            </div>

                            <div class="flex flex-col rounded-2xl border border-border/60 bg-background/80 p-4 text-left shadow-sm sm:p-5">
                                <div class="flex flex-wrap items-center justify-between gap-2">
                                    <div class="flex min-w-0 items-center gap-2.5">
                                        <div class="flex size-8 flex-none items-center justify-center rounded-lg bg-gradient-to-br from-teal-500 to-emerald-500 text-white">
                                            <CalendarClock class="size-4" />
                                        </div>
                                        <span class="font-display text-sm font-bold">Calendar</span>
                                    </div>
                                    <span class="rounded-full border border-border/60 px-2.5 py-0.5 text-[10px] font-semibold tracking-wider text-muted-foreground uppercase">02 · Publish</span>
                                </div>
                                <div class="mt-4 grid grid-cols-7 gap-1 sm:gap-1.5">
                                    <div v-for="d in 7" :key="d" class="flex flex-col items-center gap-1.5 rounded-lg border border-border/40 bg-card/50 py-2">
                                        <span class="text-[9px] font-semibold text-muted-foreground/70">{{ ['M', 'T', 'W', 'T', 'F', 'S', 'S'][d - 1] }}</span>
                                        <span
                                            class="size-1.5 rounded-full"
                                            :class="[2, 3, 5].includes(d) ? 'bg-emerald-400' : [4, 6].includes(d) ? 'bg-sky-400' : 'bg-border'"
                                        />
                                    </div>
                                </div>
                                <div class="mt-3 space-y-2 text-[12px]">
                                    <div class="flex flex-col gap-1 rounded-lg border border-border/50 bg-card/60 px-3 py-2 min-[400px]:flex-row min-[400px]:items-center min-[400px]:justify-between">
                                        <span class="font-medium">LinkedIn · Tue 09:00</span>
                                        <span class="inline-flex items-center gap-1 font-medium text-emerald-500"><span class="size-1.5 rounded-full bg-emerald-400" /> Published</span>
                                    </div>
                                    <div class="flex flex-col gap-1 rounded-lg border border-border/50 bg-card/60 px-3 py-2 min-[400px]:flex-row min-[400px]:items-center min-[400px]:justify-between">
                                        <span class="font-medium">Instagram · Wed 18:30</span>
                                        <span class="inline-flex items-center gap-1 font-medium text-sky-500"><span class="size-1.5 rounded-full bg-sky-400" /> Scheduled</span>
                                    </div>
                                    <div class="flex flex-col gap-1 rounded-lg border border-border/50 bg-card/60 px-3 py-2 min-[400px]:flex-row min-[400px]:items-center min-[400px]:justify-between">
                                        <span class="font-medium">X · Thu 11:00</span>
                                        <span class="inline-flex items-center gap-1 font-medium text-sky-500"><span class="size-1.5 rounded-full bg-sky-400" /> Scheduled</span>
                                    </div>
                                </div>
                            </div>

                            <div class="hidden items-center lg:flex">
                                <div class="flex size-8 items-center justify-center rounded-full border border-border/60 bg-card text-muted-foreground">
                                    <ChevronRight class="size-4" />
                                </div>
                            </div>

                            <div class="flex flex-col rounded-2xl border border-border/60 bg-background/80 p-4 text-left shadow-sm sm:p-5">
                                <div class="flex flex-wrap items-center justify-between gap-2">
                                    <div class="flex min-w-0 items-center gap-2.5">
                                        <div class="flex size-8 flex-none items-center justify-center rounded-lg bg-gradient-to-br from-amber-500 to-orange-500 text-white">
                                            <ChartSpline class="size-4" />
                                        </div>
                                        <span class="font-display text-sm font-bold">Analytics</span>
                                    </div>
                                    <span class="rounded-full border border-border/60 px-2.5 py-0.5 text-[10px] font-semibold tracking-wider text-muted-foreground uppercase">03 · Learn</span>
                                </div>
                                <div class="mt-4 rounded-xl border border-border/60 bg-card/70 p-3.5">
                                    <div class="text-[11px] font-semibold tracking-wide text-muted-foreground uppercase">Top post · this week</div>
                                    <div class="mt-1 truncate text-[13px] font-semibold">"Onboarding shouldn't feel like homework"</div>
                                    <div class="mt-3 flex h-12 items-end gap-1">
                                        <div
                                            v-for="(h, i) in topPostBars"
                                            :key="i"
                                            class="flex-1 rounded-sm bg-gradient-to-t from-amber-500/70 to-orange-400"
                                            :style="{ height: h + '%' }"
                                        />
                                    </div>
                                </div>
                                <div class="mt-3 flex items-center gap-4 text-[12px] font-medium text-muted-foreground">
                                    <span class="inline-flex items-center gap-1"><Heart class="size-3.5 text-rose-400" /> 1.2k</span>
                                    <span class="inline-flex items-center gap-1"><MessageCircle class="size-3.5 text-sky-400" /> 214</span>
                                    <span class="inline-flex items-center gap-1"><Eye class="size-3.5 text-teal-400" /> 48k</span>
                                </div>
                                <div class="mt-auto pt-4">
                                    <div class="inline-flex w-full items-center justify-center gap-1.5 rounded-xl border border-violet-400/50 bg-violet-500/10 px-4 py-2.5 text-sm font-semibold text-violet-600 dark:text-violet-300">
                                        <Repeat class="size-4" /> Create similar
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="mt-5 hidden items-center gap-4 lg:flex">
                            <svg class="h-3 flex-1" preserveAspectRatio="none" viewBox="0 0 100 4">
                                <line x1="100" y1="2" x2="0" y2="2" stroke="currentColor" stroke-width="1.5" class="ct-dash-flow text-violet-400/60" />
                            </svg>
                            <span class="inline-flex flex-none items-center gap-2 rounded-full border border-violet-400/40 bg-violet-500/10 px-4 py-1.5 text-xs font-semibold text-violet-600 dark:text-violet-300">
                                <Bot class="size-3.5" />
                                Autopilot: your best performer becomes the brief for the next post
                            </span>
                            <svg class="h-3 flex-1" preserveAspectRatio="none" viewBox="0 0 100 4">
                                <line x1="100" y1="2" x2="0" y2="2" stroke="currentColor" stroke-width="1.5" class="ct-dash-flow text-violet-400/60" />
                            </svg>
                        </div>

                        <div class="mt-4 flex justify-center lg:hidden">
                            <span class="inline-flex max-w-full items-start gap-2 rounded-2xl border border-violet-400/40 bg-violet-500/10 px-3 py-2.5 text-left text-xs font-semibold text-violet-600 sm:items-center sm:rounded-full sm:px-4 dark:text-violet-300">
                                <Bot class="mt-0.5 size-3.5 flex-none sm:mt-0" />
                                <span class="min-w-0 leading-snug">Autopilot: your best performer becomes the brief for the next post</span>
                            </span>
                        </div>
                    </div>

                    <div class="absolute -top-3 right-2 hidden items-center gap-2 rounded-full border border-border bg-card/90 px-3 py-1.5 text-xs font-medium shadow-sm backdrop-blur md:flex">
                        <Zap class="size-3.5 text-amber-400" />
                        11 posts from one podcast episode
                    </div>
                    <div class="mt-3 flex justify-center md:hidden">
                        <span class="inline-flex items-center gap-2 rounded-full border border-border bg-card/90 px-3 py-1.5 text-xs font-medium shadow-sm">
                            <Zap class="size-3.5 text-amber-400" />
                            11 posts from one podcast episode
                        </span>
                    </div>
                </div>
            </div>
        </section>

        <!-- Formats + channels — light bridge between proof and product -->
        <section class="relative bg-white dark:bg-zinc-950">
            <div class="relative bg-gradient-to-r from-violet-50/80 via-fuchsia-50/50 to-cyan-50/80 py-5 dark:from-violet-950/30 dark:via-fuchsia-950/20 dark:to-cyan-950/30">
                <div class="overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_12%,black_88%,transparent)]">
                    <div class="ct-marquee flex w-max gap-3">
                        <template v-for="n in 2" :key="n">
                            <span
                                v-for="f in formats"
                                :key="n + f"
                                class="inline-flex flex-none items-center gap-1.5 rounded-full border border-violet-200/60 bg-white/80 px-4 py-1.5 text-sm font-medium text-zinc-600 shadow-sm dark:border-violet-500/20 dark:bg-zinc-900/60 dark:text-zinc-300"
                            >
                                <Sparkles class="size-3.5 text-violet-400" /> {{ f }}
                            </span>
                        </template>
                    </div>
                </div>
            </div>
            <div class="relative mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-10">
                <p class="px-2 text-center text-[11px] font-semibold tracking-[0.16em] text-cyan-600 uppercase sm:text-xs sm:tracking-[0.2em] dark:text-cyan-300">
                    Publishes natively, everywhere your audience lives
                </p>
                <div class="mt-5 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 sm:gap-x-10 sm:gap-y-3">
                    <span
                        v-for="c in channels"
                        :key="c"
                        class="font-display text-base font-bold text-zinc-600 transition hover:bg-gradient-to-r hover:from-violet-500 hover:to-cyan-500 hover:bg-clip-text hover:text-transparent sm:text-lg dark:text-zinc-400"
                    >
                        {{ c }}
                    </span>
                </div>
            </div>
        </section>

        <!-- Modules — cyan/violet: systems wired together -->
        <section id="modules" class="ct-band-modules relative scroll-mt-20 overflow-hidden">
            <div class="relative mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-16 md:py-20">
                <div class="mx-auto max-w-2xl text-center">
                    <p class="text-xs font-bold tracking-[0.2em] text-cyan-500 uppercase dark:text-cyan-300">
                        Four modules · One loop
                    </p>
                    <h2 class="font-display mt-3 text-3xl font-extrabold tracking-tight text-balance sm:text-4xl md:text-5xl">
                        Everything your content needs,
                        <span class="ct-gradient-text">wired together</span>
                    </h2>
                    <p class="mt-4 text-base text-muted-foreground sm:text-lg">
                        Each module is strong on its own. Connected, they replace your content ops.
                    </p>
                </div>

                <div class="mt-8 grid gap-4 md:grid-cols-2">
                    <div
                        v-for="m in modules"
                        :key="m.title"
                        class="group relative overflow-hidden rounded-2xl border border-cyan-200/40 bg-white/85 p-5 shadow-lg shadow-cyan-500/5 backdrop-blur transition duration-300 hover:-translate-y-1 hover:shadow-xl sm:rounded-3xl sm:p-7 dark:border-cyan-500/15 dark:bg-zinc-900/70"
                        :class="m.glow"
                    >
                        <div class="flex items-center justify-between">
                            <div
                                class="flex size-12 items-center justify-center rounded-2xl bg-gradient-to-br text-white shadow-lg"
                                :class="m.iconBg"
                            >
                                <component :is="m.icon" class="size-5" />
                            </div>
                            <span class="rounded-full border border-border/60 bg-background/50 px-3 py-1 text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
                                {{ m.tag }}
                            </span>
                        </div>
                        <h3 class="font-display mt-4 text-xl font-bold tracking-tight sm:mt-5 sm:text-2xl">{{ m.title }}</h3>
                        <p class="mt-2 text-[15px] leading-relaxed text-muted-foreground">{{ m.desc }}</p>
                        <ul class="mt-4 space-y-2 text-sm">
                            <li v-for="p in m.points" :key="p" class="flex items-start gap-2.5">
                                <Check class="mt-0.5 size-4 flex-none text-teal-500 dark:text-teal-400" />
                                <span>{{ p }}</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </section>

        <!-- Autopilot — indigo/violet: overnight magic, still easy on the eyes -->
        <section class="ct-band-autopilot relative overflow-hidden">
            <div class="relative mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-16 md:py-20">
                <div class="relative overflow-hidden rounded-2xl border border-violet-200/50 bg-white/85 p-5 shadow-xl shadow-violet-500/10 backdrop-blur sm:rounded-[2rem] sm:p-8 md:p-12 dark:border-violet-500/20 dark:bg-zinc-900/75">
                    <div class="relative grid items-center gap-6 sm:gap-8 lg:grid-cols-2">
                        <div>
                            <p class="text-xs font-bold tracking-[0.2em] text-violet-500 uppercase dark:text-violet-300">
                                Autopilot
                            </p>
                            <h2 class="font-display mt-3 text-3xl font-extrabold tracking-tight text-balance sm:text-4xl md:text-5xl">
                                Your marketing runs
                                <span class="ct-gradient-text">while you sleep</span>
                            </h2>
                            <p class="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
                                Around the clock, CreativeTint syncs engagement and comments from every platform, spots the posts that outperform your average, and drafts follow-ups in your voice — ready for approval when you wake up.
                            </p>
                            <ul class="mt-6 space-y-2.5 text-[15px]">
                                <li class="flex items-start gap-3">
                                    <Check class="mt-0.5 size-5 flex-none text-teal-500 dark:text-teal-400" />
                                    <span>Engagement, comments &amp; reactions synced hourly</span>
                                </li>
                                <li class="flex items-start gap-3">
                                    <Check class="mt-0.5 size-5 flex-none text-teal-500 dark:text-teal-400" />
                                    <span>Breakout posts detected against your own baseline</span>
                                </li>
                                <li class="flex items-start gap-3">
                                    <Check class="mt-0.5 size-5 flex-none text-teal-500 dark:text-teal-400" />
                                    <span>Follow-ups drafted in your brand voice — you approve, it ships</span>
                                </li>
                            </ul>
                        </div>

                        <div class="rounded-2xl border border-border/60 bg-background/70 p-4 shadow-sm backdrop-blur sm:p-5">
                            <div class="flex flex-wrap items-center justify-between gap-2">
                                <span class="font-display text-sm font-bold">Overnight activity</span>
                                <span class="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 px-3 py-1 text-[11px] font-semibold text-emerald-500">
                                    <span class="size-1.5 animate-pulse rounded-full bg-emerald-400" /> Autopilot on
                                </span>
                            </div>
                            <div class="mt-4 space-y-2">
                                <div
                                    v-for="a in activity"
                                    :key="a.time"
                                    class="flex items-start gap-2.5 rounded-xl border p-3 text-sm sm:gap-3"
                                    :class="
                                        a.hot
                                            ? 'border-amber-400/40 bg-amber-500/[0.07]'
                                            : a.cta
                                              ? 'border-violet-400/40 bg-violet-500/[0.07]'
                                              : 'border-border/50 bg-card/50'
                                    "
                                >
                                    <span class="mt-0.5 flex-none font-mono text-[11px] text-muted-foreground sm:text-xs">{{ a.time }}</span>
                                    <span class="min-w-0 leading-snug" :class="a.hot ? 'font-medium' : ''">{{ a.text }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </section>

        <!-- How + testimonials — teal/amber: human trust -->
        <section id="how" class="ct-band-human relative scroll-mt-20 overflow-hidden">
            <div class="relative mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-16 md:py-20">
                <div class="mx-auto max-w-2xl text-center">
                    <p class="text-xs font-bold tracking-[0.2em] text-teal-500 uppercase dark:text-teal-300">
                        How it works
                    </p>
                    <h2 class="font-display mt-3 text-3xl font-extrabold tracking-tight text-balance sm:text-4xl md:text-5xl">
                        From brand voice to feedback loop
                        <span class="ct-gradient-text">in one afternoon</span>
                    </h2>
                </div>

                <div class="mt-8 grid gap-4 md:grid-cols-3">
                    <div
                        v-for="(step, i) in steps"
                        :key="step.n"
                        class="relative overflow-hidden rounded-2xl border border-teal-200/40 bg-white/85 p-5 shadow-md shadow-teal-500/5 sm:rounded-3xl sm:p-7 dark:border-teal-500/15 dark:bg-zinc-900/70"
                    >
                        <div class="font-display ct-gradient-text text-5xl font-extrabold">{{ step.n }}</div>
                        <h3 class="font-display mt-4 text-xl font-bold tracking-tight">{{ step.t }}</h3>
                        <p class="mt-2 text-sm leading-relaxed text-muted-foreground">{{ step.d }}</p>
                        <ChevronRight
                            v-if="i < 2"
                            class="absolute top-8 -right-3 hidden size-8 text-border md:block"
                        />
                    </div>
                </div>

                <div class="mx-auto mt-12 max-w-2xl text-center sm:mt-14">
                    <p class="text-xs font-bold tracking-[0.2em] text-teal-500 uppercase dark:text-teal-300">
                        Loved by early teams
                    </p>
                    <h2 class="font-display mt-3 text-3xl font-extrabold tracking-tight text-balance sm:text-4xl md:text-5xl">
                        Don't take our word for it
                    </h2>
                </div>

                <div class="mt-8 grid gap-4 md:grid-cols-3">
                    <div
                        v-for="t in testimonials"
                        :key="t.name"
                        class="rounded-2xl border border-amber-200/40 bg-white/85 p-5 shadow-md shadow-amber-500/5 backdrop-blur sm:rounded-3xl sm:p-6 dark:border-amber-500/15 dark:bg-zinc-900/70"
                    >
                        <div class="flex gap-0.5 text-amber-400">
                            <Star v-for="n in 5" :key="n" class="size-4 fill-current" />
                        </div>
                        <blockquote class="mt-4 text-[15px] leading-relaxed text-foreground/90">
                            "{{ t.quote }}"
                        </blockquote>
                        <div class="mt-5 flex items-center gap-3">
                            <div class="size-11 rounded-full bg-gradient-to-br" :class="t.avatar" />
                            <div>
                                <div class="text-sm font-bold">{{ t.name }}</div>
                                <div class="text-xs text-muted-foreground">{{ t.role }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Pricing — clear decision surface -->
        <section id="pricing" class="ct-band-decision relative scroll-mt-20 overflow-hidden">
            <div class="relative mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-16 md:py-20">
                <div class="mx-auto max-w-2xl text-center">
                    <p class="text-xs font-bold tracking-[0.2em] text-violet-500 uppercase dark:text-violet-300">
                        Pricing
                    </p>
                    <h2 class="font-display mt-3 text-3xl font-extrabold tracking-tight text-balance sm:text-4xl md:text-5xl">
                        One price.
                        <span class="ct-gradient-text">Everything included.</span>
                    </h2>
                    <p class="mt-4 text-base text-muted-foreground sm:text-lg">
                        No tiers to compare. Manual create, edit, schedule, and publish are unlimited.
                        Autopilot runs on a fair-use limit we'll tune as we grow.
                    </p>
                </div>

                <div class="mx-auto mt-8 max-w-lg">
                    <div class="relative flex flex-col rounded-2xl border border-violet-300/60 bg-white/90 p-6 shadow-xl backdrop-blur ct-glow-violet sm:rounded-3xl sm:p-8 md:p-9 dark:border-violet-400/40 dark:bg-zinc-900/80">
                        <div class="absolute -top-3.5 left-1/2 -translate-x-1/2 rounded-full bg-gradient-to-br from-violet-500 to-cyan-500 px-4 py-1 text-[11px] font-bold tracking-wider whitespace-nowrap text-white uppercase shadow-lg">
                            Simple pricing
                        </div>
                        <div class="font-display text-lg font-bold">{{ pricing.name }}</div>
                        <div class="mt-3 flex items-baseline gap-1">
                            <span class="font-display text-5xl font-extrabold tracking-tight sm:text-6xl">${{ pricing.price }}</span>
                            <span class="text-sm text-muted-foreground">/ month</span>
                        </div>
                        <p class="mt-2 text-sm text-muted-foreground">{{ pricing.description }}</p>
                        <ul class="mt-7 space-y-2.5 text-sm">
                            <li v-for="f in pricing.features" :key="f" class="flex items-start gap-2.5">
                                <Check class="mt-0.5 size-4 flex-none text-teal-500 dark:text-teal-400" />
                                <span>{{ f }}</span>
                            </li>
                        </ul>
                        <Link
                            :href="register()"
                            class="mt-8 inline-flex items-center justify-center gap-1.5 rounded-full bg-gradient-to-br from-violet-500 to-cyan-500 px-5 py-3.5 text-sm font-semibold text-white shadow-lg shadow-violet-500/30 transition hover:opacity-90"
                        >
                            {{ pricing.cta }} <ArrowRight class="size-4" />
                        </Link>
                        <p class="mt-3 text-center text-xs text-muted-foreground">
                            Cancel anytime from Billing. No annual lock-in.
                        </p>
                    </div>
                </div>

                <div id="faq" class="mx-auto mt-12 max-w-5xl scroll-mt-20 sm:mt-14">
                    <h2 class="font-display text-center text-3xl font-extrabold tracking-tight sm:text-4xl">
                        Questions, answered
                    </h2>
                    <p class="mx-auto mt-3 max-w-xl text-center text-sm text-muted-foreground sm:text-base">
                        Everything a marketing team usually asks before switching tools.
                    </p>
                    <div class="mt-6 grid gap-3 sm:mt-8 lg:grid-cols-2 lg:items-start">
                        <details
                            v-for="f in faqs"
                            :key="f.q"
                            class="group rounded-2xl border border-violet-200/50 bg-white/80 p-4 shadow-sm transition hover:border-violet-300 sm:p-5 dark:border-violet-500/15 dark:bg-zinc-900/60 dark:hover:border-violet-500/30"
                        >
                            <summary class="flex cursor-pointer items-start justify-between gap-3 text-left text-sm font-semibold sm:text-[15px] [&::-webkit-details-marker]:hidden">
                                <span class="min-w-0">{{ f.q }}</span>
                                <ChevronRight class="mt-0.5 size-4 flex-none text-muted-foreground transition group-open:rotate-90" />
                            </summary>
                            <p class="mt-3 text-sm leading-relaxed text-muted-foreground">
                                {{ f.a }}
                            </p>
                        </details>
                    </div>
                </div>
            </div>
        </section>

        <!-- CTA -->
        <section class="relative overflow-hidden border-t border-violet-200/50 dark:border-violet-500/20">
            <div class="absolute inset-0 bg-gradient-to-b from-white via-violet-50/80 to-cyan-50/70 dark:from-zinc-950 dark:via-violet-950/40 dark:to-cyan-950/30" />
            <div class="pointer-events-none absolute top-0 left-1/2 size-[28rem] -translate-x-1/2 -translate-y-1/3 rounded-full bg-fuchsia-400/15 blur-3xl dark:bg-fuchsia-500/10" aria-hidden="true" />
            <svg class="pointer-events-none absolute inset-0 h-full w-full text-violet-400/15" viewBox="0 0 1200 360" fill="none" preserveAspectRatio="none" aria-hidden="true">
                <path d="M-30 310C181 149 339 120 513 212C699 310 869 274 1230 55" stroke="currentColor" stroke-width="2" />
                <path d="M-40 350C182 194 357 170 527 253C714 344 901 296 1230 103" stroke="currentColor" class="text-cyan-400/15" />
            </svg>
            <div class="relative mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-14">
                <div class="relative overflow-hidden rounded-2xl border border-violet-200/60 bg-white/80 p-6 text-center shadow-xl shadow-violet-500/10 backdrop-blur sm:rounded-[2rem] sm:p-10 md:p-14 dark:border-violet-500/20 dark:bg-zinc-900/70">
                    <div class="pointer-events-none absolute -top-20 -right-16 size-56 rounded-full bg-cyan-400/20 blur-3xl" aria-hidden="true" />
                    <div class="pointer-events-none absolute -bottom-24 -left-16 size-56 rounded-full bg-violet-400/20 blur-3xl" aria-hidden="true" />
                    <h2 class="relative font-display text-3xl font-extrabold tracking-tight text-balance sm:text-4xl md:text-5xl">
                        Stop guessing what to post next.
                        <span class="ct-gradient-text">Your data already knows.</span>
                    </h2>
                    <p class="relative mx-auto mt-4 max-w-xl text-base text-zinc-600 sm:text-lg dark:text-zinc-300">
                        Start a 7-day Pro trial. Create, publish, and let CreativeTint learn what your audience actually wants.
                    </p>
                    <Link
                        :href="register()"
                        class="group relative mt-6 inline-flex w-full max-w-sm items-center justify-center gap-2 rounded-full bg-gradient-to-br from-violet-500 via-fuchsia-500 to-cyan-500 px-6 py-3.5 text-base font-semibold text-white transition hover:scale-[1.02] hover:opacity-95 sm:mt-8 sm:w-auto sm:px-9"
                    >
                        Start your 7-day free trial
                        <ArrowRight class="size-4 transition group-hover:translate-x-0.5" />
                    </Link>
                </div>
            </div>
        </section>
        </main>

        <MarketingFooter />
    </div>
</template>
